import React from 'react';
import { useHistory } from 'react-router-dom';
import { GoFileDirectory } from 'react-icons/go';
import { BRAND, INK, STATUS } from 'Utils/styleTokens';
import { encodeFolderPathForUrl } from './folderPathUtils';
import { TYPE_COLORS, TYPE_LABELS } from './constants';

function formatSize(sizeGB) {
  if (sizeGB == null) return '—';
  if (sizeGB >= 0.1) return `${sizeGB}GB`;
  return `${Math.round(sizeGB * 1024)}MB`;
}

const TypePill = ({ type }) => {
  const tc = TYPE_COLORS[type];
  if (!tc) {
    return <span className='metadata-tpill' style={{ background: STATUS.neutral.bg, color: STATUS.neutral.text }}>{type}</span>;
  }
  return (
    <span className='metadata-tpill' style={{ background: tc.bg, color: tc.text }}>
      {TYPE_LABELS[type] || type}
    </span>
  );
};

const MatchBar = ({ match }) => {
  if (match == null || !Number.isFinite(Number(match))) {
    return <span style={{ fontSize: 11, color: INK[400] }}>—</span>;
  }
  const pct = Math.max(0, Math.min(100, Number(match)));
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end' }}>
      <div
        style={{
          width: 48,
          height: 3,
          background: INK[150],
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: INK[400],
            opacity: 0.7,
            borderRadius: 2,
          }}
        />
      </div>
      <span style={{ fontSize: 11, color: INK[500] }} data-testid='instance-match'>{pct}%</span>
    </div>
  );
};

// File sources map their short types to 'file'/'csv' in adaptSources; relational
// engines map to 'pg'/'mysql'/'mongo'. A file source's structural unit is a folder,
// not a table, so the column is relabelled when every instance is file-based.
const FILE_SHORT_TYPES = new Set(['file', 'csv']);

/**
 * `folderMatches` appends folder rows to the instance list: folders elsewhere in the
 * estate whose files matched this identity's commercial schema (e.g. SugarCRM-shaped
 * CSVs under the real SugarCRM database). Entries carry {folderPath, matchPct,
 * sourceName, sourceDataSourceId, covered}. Row click opens the folder drill-down in
 * its PARENT (profiled) data source — the page that shows the source and the matched
 * path together. Covered folders (a data source is rooted there) look like any other
 * row; uncovered ones render faded with an inline "Create data source" action
 * (`onCreateSource`).
 *
 * `commercialSchemaName` is the identity's identified commercial product ("SugarCRM
 * Community"). When set, the Region column (never populated by the meta-analysis
 * pipeline) is repurposed as the Commercial schema column: instance rows show the
 * identity's product, folder rows show the product their own match hit.
 */
const InstanceTable = ({ sources, folderMatches, onCreateSource, commercialSchemaName }) => {
  const history = useHistory();
  const folders = folderMatches || [];
  const folderSchemaName = (fm) =>
    [fm.vendorName, fm.productName].filter(Boolean).join(' ') || null;
  const showSchemaColumn =
    !!commercialSchemaName || folders.some((fm) => folderSchemaName(fm));
  if ((!sources || sources.length === 0) && folders.length === 0) {
    return <div className='metadata-empty'>No instances for this identity.</div>;
  }
  const handleRowClick = (dataSourceId) => {
    if (dataSourceId == null) return;
    history.push(`/metadata/source/${encodeURIComponent(String(dataSourceId))}`);
  };
  const openFolder = (fm) => {
    if (fm.sourceDataSourceId == null) return;
    history.push(
      `/metadata/source/${encodeURIComponent(String(fm.sourceDataSourceId))}/folder/${encodeFolderPathForUrl(fm.folderPath)}`
    );
  };
  const allFileBased = (sources || []).every((s) => FILE_SHORT_TYPES.has(s.type));
  return (
    <div style={{ overflow: 'auto', flex: 1 }}>
      <table className='metadata-itbl'>
        <thead>
          <tr>
            <th>Instance</th>
            <th>{showSchemaColumn ? 'Commercial schema' : 'Region'}</th>
            <th>Type</th>
            <th className='num'>Size</th>
            <th className='num'>{allFileBased ? 'Folders' : 'Tables'}</th>
            <th className='num'>Rows</th>
            <th className='num'>Match</th>
            <th className='num'>First seen</th>
          </tr>
        </thead>
        <tbody>
          {(sources || []).map((s, i) => {
            const clickable = s.dataSourceId != null;
            return (
              <tr
                key={`${s.dataSourceId || s.name}-${i}`}
                data-testid='instance-row'
                onClick={clickable ? () => handleRowClick(s.dataSourceId) : undefined}
                style={clickable ? { cursor: 'pointer' } : undefined}
                title={clickable ? 'Open data source detail' : undefined}
              >
                <td>
                  <div>{s.name}</div>
                  {s.database && (
                    <div
                      style={{
                        fontStyle: 'italic',
                        fontSize: 11,
                        color: INK[400],
                        fontWeight: 400,
                        marginTop: 1,
                      }}
                    >
                      {s.database}
                    </div>
                  )}
                </td>
                <td style={{ color: INK[500], fontSize: 11 }}>
                  {showSchemaColumn ? (commercialSchemaName || '—') : s.region}
                </td>
                <td><TypePill type={s.type} /></td>
                <td className='num' style={{ color: INK[500] }}>{formatSize(s.sizeGB)}</td>
                <td className='num' style={{ color: INK[500] }}>{s.tables != null ? s.tables : '—'}</td>
                <td className='num' style={{ color: INK[500] }}>{s.rows}</td>
                <td className='num'><MatchBar match={s.match} /></td>
                <td className='num' style={{ color: INK[500], fontFamily: 'monospace', fontSize: 11 }}>{s.created}</td>
              </tr>
            );
          })}
          {folders.map((fm) => (
            <tr
              key={fm.folderPath}
              data-testid='matched-folder-row'
              onClick={() => openFolder(fm)}
              title='Open this folder in its parent data source'
              style={{ cursor: 'pointer', opacity: fm.covered ? 1 : 0.55 }}
            >
              <td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <GoFileDirectory style={{ flexShrink: 0, opacity: 0.7 }} />
                  <span style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
                    {fm.folderPath}
                  </span>
                </div>
                <div
                  style={{
                    fontStyle: 'italic',
                    fontSize: 11,
                    color: INK[400],
                    fontWeight: 400,
                    marginTop: 1,
                  }}
                >
                  {fm.covered ? (
                    <span data-testid='matched-folder-covered'>
                      Covered by {fm.covered.name || `source ${fm.covered.id}`}
                    </span>
                  ) : (
                    <>
                      {fm.sourceName ? `in ${fm.sourceName} · ` : ''}no data source ·{' '}
                      <span
                        role='link'
                        tabIndex={0}
                        data-testid='matched-folder-create'
                        title='Create a data source rooted at this folder'
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onCreateSource) onCreateSource(fm);
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.click(); }}
                        style={{
                          color: BRAND.blue,
                          cursor: 'pointer',
                          textDecoration: 'underline',
                          textDecorationColor: 'rgba(49,93,255,0.35)',
                        }}
                      >
                        Create data source →
                      </span>
                    </>
                  )}
                </div>
              </td>
              <td style={{ color: INK[500], fontSize: 11 }}>{folderSchemaName(fm) || '—'}</td>
              <td><TypePill type='file' /></td>
              <td className='num' style={{ color: INK[500] }}>—</td>
              <td className='num' style={{ color: INK[500] }}>—</td>
              <td className='num' style={{ color: INK[500] }}>—</td>
              <td className='num'><MatchBar match={fm.matchPct} /></td>
              <td className='num' style={{ color: INK[500], fontFamily: 'monospace', fontSize: 11 }}>—</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default InstanceTable;
